import React, { useState, useCallback } from 'react';
import { Upload, FileText, Trash2, Download, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../common/Button';
import Modal from '../common/Modal';
import { validarDocumento } from '../../utils/validators';

/**
 * Componente modal para carga de documentos
 * Permite cargar múltiples documentos con validaciones
 */
const DocumentosModal = ({
  isOpen,
  onClose,
  estudianteId,
  onDocumentosCargados,
  tiposDocumentosDisponibles = [
    { id: 'certificado_nacimiento', nombre: 'Certificado de Nacimiento', requerido: true },
    { id: 'cedula_estudiante', nombre: 'Cédula/Documento de Identidad', requerido: true },
    { id: 'certificado_vacunas', nombre: 'Certificado de Vacunas', requerido: false },
    { id: 'cartilla_salud', nombre: 'Cartilla de Salud', requerido: false },
    { id: 'otros', nombre: 'Otros Documentos', requerido: false }
  ]
}) => {
  const [documentos, setDocumentos] = useState({});
  const [errores, setErrores] = useState({});
  const [cargando, setCargando] = useState(false);
  const [documentosCargados, setDocumentosCargados] = useState([]);

  const tiposArchivosPermitidos = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
  const tamañoMaximoMB = 5;

  const manejarSeleccionArchivo = useCallback((tipoDocumento, archivo) => {
    if (!archivo) {
      setDocumentos(prev => {
        const nuevo = { ...prev };
        delete nuevo[tipoDocumento];
        return nuevo;
      });
      setErrores(prev => {
        const nuevo = { ...prev };
        delete nuevo[tipoDocumento];
        return nuevo;
      });
      return;
    }

    // Validar documento
    const errorValidacion = validarDocumento(archivo, tiposArchivosPermitidos, tamañoMaximoMB);
    
    if (errorValidacion) {
      setErrores(prev => ({
        ...prev,
        [tipoDocumento]: errorValidacion
      }));
      setDocumentos(prev => {
        const nuevo = { ...prev };
        delete nuevo[tipoDocumento];
        return nuevo;
      });
    } else {
      setErrores(prev => {
        const nuevo = { ...prev };
        delete nuevo[tipoDocumento];
        return nuevo;
      });
      setDocumentos(prev => ({
        ...prev,
        [tipoDocumento]: archivo
      }));
    }
  }, []);

  const cargarDocumentos = async () => {
    // Validar que se han seleccionado documentos
    if (Object.keys(documentos).length === 0) {
      setErrores({ general: 'Debe seleccionar al menos un documento' });
      return;
    }

    // Validar documentos requeridos
    const tiposRequeridos = tiposDocumentosDisponibles
      .filter(t => t.requerido)
      .map(t => t.id);

    const documentosFaltantes = tiposRequeridos.filter(tipo => !documentos[tipo]);
    if (documentosFaltantes.length > 0) {
      const nombresDocumentos = documentosFaltantes
        .map(tipo => tiposDocumentosDisponibles.find(t => t.id === tipo)?.nombre)
        .join(', ');
      setErrores({ general: `Documentos requeridos faltantes: ${nombresDocumentos}` });
      return;
    }

    setCargando(true);
    try {
      // Aquí iría la lógica para cargar los documentos al servidor
      // Por ahora, simularemos una carga exitosa
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Simular documentos cargados
      const nuevosCargados = Object.entries(documentos).map(([tipo, archivo]) => {
        const tipoDoc = tiposDocumentosDisponibles.find(t => t.id === tipo);
        return {
          id: Date.now() + Math.random(),
          tipo,
          nombre: tipoDoc?.nombre || tipo,
          archivo: archivo.name,
          tamaño: archivo.size,
          fechaCarga: new Date().toLocaleDateString()
        };
      });

      setDocumentosCargados(prev => [...prev, ...nuevosCargados]);
      
      // Notificar al componente padre
      if (onDocumentosCargados) {
        onDocumentosCargados(nuevosCargados);
      }

      // Limpiar formulario
      setDocumentos({});
      setErrores({});

      // Cerrar modal después de 2 segundos
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setErrores({ general: 'Error al cargar los documentos. Por favor intente nuevamente.' });
    } finally {
      setCargando(false);
    }
  };

  const eliminarDocumentoCargado = useCallback((id) => {
    setDocumentosCargados(prev => prev.filter(doc => doc.id !== id));
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Cargar Documentos del Estudiante"
      size="lg"
    >
      <div className="space-y-6">
        {/* Información */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Nota:</strong> Los documentos marcados con <span className="text-red-600">*</span> son obligatorios.
            Formatos permitidos: PDF, JPEG, PNG (máx. {tamañoMaximoMB} MB)
          </p>
        </div>

        {/* Errores generales */}
        {errores.general && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{errores.general}</p>
          </div>
        )}

        {/* Documentos cargados exitosamente */}
        {documentosCargados.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Documentos Cargados ({documentosCargados.length})</h4>
            {documentosCargados.map(doc => (
              <div key={doc.id} className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">{doc.nombre}</p>
                    <p className="text-xs text-gray-600">
                      {doc.archivo} • {(doc.tamaño / 1024).toFixed(2)} KB • {doc.fechaCarga}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => eliminarDocumentoCargado(doc.id)}
                  className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Formulario de carga */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Seleccione Documentos</h4>
          
          {tiposDocumentosDisponibles.map((tipo) => (
            <div key={tipo.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2">
                  <FileText size={18} className="text-gray-600" />
                  <span className="font-medium text-gray-900">
                    {tipo.nombre}
                    {tipo.requerido && <span className="text-red-600 ml-1">*</span>}
                  </span>
                </label>
                {documentos[tipo.id] && (
                  <CheckCircle size={18} className="text-green-600" />
                )}
              </div>

              <div className="relative">
                <input
                  type="file"
                  accept={tiposArchivosPermitidos.join(',')}
                  onChange={(e) => manejarSeleccionArchivo(tipo.id, e.target.files?.[0])}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <div className="flex items-center justify-center gap-2">
                    <Upload size={18} className="text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {documentos[tipo.id]
                        ? documentos[tipo.id].name
                        : 'Haga clic para seleccionar archivo'}
                    </span>
                  </div>
                </div>
              </div>

              {errores[tipo.id] && (
                <p className="text-xs text-red-600 mt-2">{errores[tipo.id]}</p>
              )}
            </div>
          ))}
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3 justify-end">
          <Button
            onClick={onClose}
            variant="secondary"
            disabled={cargando}
          >
            Cancelar
          </Button>
          <Button
            onClick={cargarDocumentos}
            variant="primary"
            loading={cargando}
            disabled={Object.keys(documentos).length === 0}
          >
            <Upload size={18} /> Cargar Documentos
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentosModal;
